import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { ControlledInput } from "../../Components/ControlledInput";
import { PatternFormat } from "react-number-format";
import EnquiryInput from "../../Components/EnquiryInput";
import RequiredCard from "../../Components/RequiredCard";
import { FORM_STEP } from "../EnquiryV3";

interface IStartEnquiryProps {
    [key: string]: any;
    onNext: (payload: { data: any }) => void;
}

const careDays = [
    { label: "Mon", value: "2" },
    { label: "Tue", value: "3" },
    { label: "Wed", value: "4" },
    { label: "Thu", value: "5" },
    { label: "Fri", value: "6" },
];

const startDate = [
    {
        label: "ASAP",
        value: "ASAP",
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    },
    {
        label: "In the next 30 days",
        value: "30d",
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 30)),
    },
    {
        label: "3 months",
        value: "3m",
        startDate: new Date(),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)),
    },
    {
        label: "6 months",
        value: "6m",
        startDate: new Date(),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 6)),
    },
    {
        label: "12 months",
        value: "12m",
        startDate: new Date(),
        endDate: new Date(new Date().setMonth(new Date().getMonth() + 12)),
    },
];

type StartDate = (typeof startDate)[number];

const validationSchema = Yup.object({
    comments: Yup.string(),
    items: Yup.array().of(
        Yup.object({
            //childName: Yup.string().required('Child name is required'),
            day: Yup.string()
                .required("Day is required")
                .transform((currentValue) => currentValue.replace(/[^0-9.]/g, ""))
                .test("is-valid-day", "Day must be between 1 and 31", (value) =>
                    value ? Number(value) >= 1 && Number(value) <= 31 : false
                ),
            month: Yup.string()
                .required("Month is required")
                .transform((currentValue) => currentValue.replace(/[^0-9.]/g, ""))
                .test("is-valid-month", "Month must be between 1 and 12", (value) => {
                    return value ? Number(value) >= 1 && Number(value) <= 12 : false;
                }),
            year: Yup.string()
                .required("Year is required")
                .transform((currentValue) => currentValue.replace(/[^0-9.]/g, ""))
                .test("is-four-digits", "Year must be 4 digits", (value) => {
                    return value && value.length === 4;
                })
                .test("greater-year", "Year must be greater than 2018", (value) => {
                    return value ? Number(value) >= 2018 : false;
                }),
            dateString: Yup.string().test(
                "is-not-future-date",
                "Date cannot be in the future",
                function (value) {
                    if (!value) return true;
                    const today = new Date();
                    const [day, month, year] = value.split("-").map(Number);
                    if (!day || !month || !year) {
                        return true;
                    }
                    const inputDate = new Date(
                        Number(year),
                        Number(month) - 1,
                        Number(day)
                    );
                    return inputDate <= today;
                }
            ),
            careDays: Yup.array()
                .of(
                    Yup.object().shape({
                        label: Yup.string().required("Label is required"),
                        value: Yup.string().required("Value is required"),
                    })
                )
                .test(
                    "at-least-one-active",
                    "Please select at least one care day",
                    (careDays) => careDays.length > 0
                ),
            startDate: Yup.object({
                label: Yup.string().required("Label is required"),
                value: Yup.string().required("Value is required"),
                startDate: Yup.date().required("Value is required"),
                endDate: Yup.date().required("Value is required"),
            })
                .typeError("Please select one start date option")
                .nonNullable("Please select one start date option"),
        })
    ),
});
function ChildDetailEnquiry(props: IStartEnquiryProps) {
    const { pickerTimePersonTour } = props.enquiryDetail;
    const [currentChild, setCurrentChild] = useState(
        props.enquiryDetail.childDetail?.items?.length
            ? props.enquiryDetail.childDetail?.items?.length - 1
            : 0
    );
    const {
        control,
        handleSubmit,
        setValue,
        trigger,
        getValues,
        watch,
        formState: { errors, isSubmitted },
    } = useForm<any>({
        resolver: yupResolver(validationSchema) as any,
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            comments: props.enquiryDetail.childDetail?.comments || "",
            items: props.enquiryDetail.childDetail?.items || [
                {
                    childName: "",
                    careDays: [],
                    startDate: "",
                    day: "",
                    month: "",
                    year: "",
                },
            ],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items",
    });
    useEffect(() => {
        props.sendEventGa4({
            eventName: "enquiry_workflow",
            FormStep: FORM_STEP[2],
            Action: "start",
            ListingType: props.listingType,
        });
    }, []);
    const childDetailData = watch("items");
    const onSubmit = (data) => {
        props.sendEventGa4({
            eventName: "enquiry_workflow",
            FormStep: FORM_STEP[2],
            Action: "finish",
            ListingType: props.listingType,
        });
        props.onNext({
            data: {
                ...data,
            },
        });
    };
    const onError = (e) => {
        const invalidArray = [];

        if (e.items && Array.isArray(e.items)) {
            e.items.forEach((childErrors, index) => {
                if (childErrors) {
                    // Check birthday fields (day, month, year)
                    const hasMissingBirthday =
                        childErrors.day?.type === "required" ||
                        childErrors.month?.type === "required" ||
                        childErrors.year?.type === "required";

                    const hasInvalidBirthday =
                        childErrors.day?.type === "is-valid-day" ||
                        childErrors.month?.type === "is-valid-month" ||
                        childErrors.year?.type === "is-four-digits" ||
                        childErrors.year?.type === "greater-year" ||
                        childErrors.dateString?.type === "is-not-future-date";

                    if (hasMissingBirthday) {
                        invalidArray.push(`missing - child ${index + 1} birthday`);
                    }
                    if (hasInvalidBirthday) {
                        invalidArray.push(`invalid - child ${index + 1} birthday`);
                    }

                    // Check care days
                    if (childErrors.careDays?.type === "at-least-one-active") {
                        invalidArray.push(`missing - child ${index + 1} care days`);
                    }

                    // Check start date
                    if (
                        childErrors.startDate?.type === "required" ||
                        childErrors.startDate?.type === "typeError" ||
                        childErrors.startDate?.type === "nonNullable"
                    ) {
                        invalidArray.push(`missing - child ${index + 1} start day`);
                    }
                }
            });
        }
        invalidArray.forEach((item) => {
            props.sendEventGa4({
                eventName: "enquiry_workflow_feature",
                FeatureName: "validation error - child details",
                FeatureData: item,
            });
        });
        // props.sendEventGa4({
        //     eventName: "enquiry_workflow_feature",
        //     FeatureName: "validation error - child details",
        //     FeatureData: invalidArray,
        // });
    };
    const onSelectCareDay = (
        careDetail: { label: string; value: string },
        careDayIndex: number
    ) => {
        const currentCareDetail = getValues(`items.${careDayIndex}.careDays`);
        const exitsCareDay = currentCareDetail.find(
            (item) => item.value === careDetail.value
        );
        if (exitsCareDay) {
            const newCareDay = currentCareDetail.filter(
                (item) => item.value !== careDetail.value
            );
            setValue(`items.${careDayIndex}.careDays`, newCareDay);
        } else {
            setValue(`items.${careDayIndex}.careDays`, [
                ...currentCareDetail,
                careDetail,
            ]);
        }
    };

    const handleSelectStartDate = (date: StartDate, index: number) => {
        const currentStartDate = getValues(`items.${index}.startDate`);
        if (currentStartDate?.value === date.value)
            setValue(`items.${index}.startDate`, "");
        else {
            setValue(`items.${index}.startDate`, date);
        }
    };

    const onSelectChildChip = (index: number) => setCurrentChild(index);

    const onDeleteChild = (index: number) => {
        remove(index);
        if (index <= currentChild) {
            setCurrentChild(childDetailData.length - 2);
        }
    };

    const onAddNewChild = () => {
        append({
            childName: "",
            careDays: [],
            startDate: "",
            day: "",
            month: "",
            year: "",
        });
        setCurrentChild(childDetailData.length);
    };

    const updateDateString = (index: number, field: string, value: string) => {
        const currentValues = getValues(`items.${index}`);
        const updatedValues = {
            ...currentValues,
            [field]: value,
        };
        const { day = "dd", month = "mm", year = "yyyy" } = updatedValues;
        const newDateString = `${day.padStart(2, "0")}-${month.padStart(
            2,
            "0"
        )}-${year.padStart(4, "0000")}`;

        setValue(`items.${index}.dateString`, newDateString);
    };
    const itemsLength = Array.isArray(errors.items) ? errors.items.length : 0;

    return (
        <div className="pb-24 lg:pb-0">
            <h2 className="block text-[#3A3A3A] text-[22px] font-semibold leading-7 mb-6">
                Share your child’s details with the centre
            </h2>
            {isSubmitted && itemsLength > 0 && Array.isArray(errors.items) && (
                <RequiredCard customClass={"mb-8 md:max-w-[400px]"}>
                    <Icon
                        icon="fa6-solid:circle-exclamation"
                        width="16"
                        height="16"
                        className="flex-shrink-0 text-[#EA5743]"
                    />
                    <p className="text-[#EA5743] text-base">
                        <span className="font-semibold">
                            Some fields require your input.{" "}
                        </span>
                        Please complete all mandatory fields for
                        {errors.items.map((child, index, array) => {
                            if (child !== null) {
                                return (
                                    <span key={index}>
                                        {` child ${index + 1} `}
                                        {index < array.length - 1 && "and "}
                                    </span>
                                );
                            }
                            return null;
                        })}
                        to proceed.
                    </p>
                </RequiredCard>
            )}

            <ul className="mt-2 flex flex-wrap gap-4 items-center">
                {childDetailData.map((_, i) => {
                    return (
                        <li key={i}>
                            <p
                                className={`cursor-pointer shift-pill ${currentChild !== i &&
                                        isSubmitted &&
                                        itemsLength &&
                                        errors.items[i]
                                        ? "text-[#EA5743]"
                                        : "text-[#3A3A3A]"
                                    }  ${currentChild === i
                                        ? "font-semibold after:content-[''] after:absolute after:left-[0] after:right-[0] after:-bottom-[2px] after:h-[3px] after:bg-[#5A60EC] "
                                        : ""
                                    }`}
                                onClick={() => onSelectChildChip(i)}
                                style={{ position: "relative" }}
                            >
                                {`Child ${i + 1}`}
                                {childDetailData.length > 1 ? (
                                    <span
                                        style={{
                                            position: "absolute",
                                            top: "-15px",
                                            right: "-3px",
                                            width: "15px",
                                            background: "#fff",
                                            borderRadius: "50%",
                                            padding: "5px",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteChild(i);
                                        }}
                                    >
                                        <img src="/img/icons/close-icon.svg" />
                                    </span>
                                ) : null}
                            </p>
                        </li>
                    );
                })}
                {childDetailData.length < 5 && (
                    <svg
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={() => onAddNewChild()}
                        className="cursor-pointer"
                    >
                        <rect width="40" height="40" rx="20" fill="#F9F7F3" />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M19.872 13.078a.5.5 0 0 0-.273.154.5.5 0 0 0-.157.296c-.013.065-.018.963-.018 2.979v2.885h-2.951c-2.93 0-2.952 0-3.039.033a.6.6 0 0 0-.34.337.7.7 0 0 0-.027.198.4.4 0 0 0 .043.224c.07.155.204.274.36.321.063.02.531.023 3.014.023h2.94v2.925c0 2.044.005 2.954.018 3.02a.5.5 0 0 0 .157.296.55.55 0 0 0 .5.159.5.5 0 0 0 .302-.16.5.5 0 0 0 .158-.295c.012-.066.017-.976.017-3.02v-2.925h2.94c2.483 0 2.952-.004 3.014-.023a.6.6 0 0 0 .36-.321.4.4 0 0 0 .043-.224.7.7 0 0 0-.027-.198.6.6 0 0 0-.34-.337c-.086-.033-.109-.033-3.038-.033h-2.952v-2.885c0-2.016-.005-2.914-.017-2.98a.5.5 0 0 0-.158-.296.5.5 0 0 0-.296-.158.4.4 0 0 0-.233.005"
                            fill="#484554"
                        />
                    </svg>
                )}
            </ul>
            <form
                id="children-detail-form"
                className="space-y-4 mt-4"
                onSubmit={handleSubmit(onSubmit, onError)}
            >
                {fields.map((field, index) => {
                    return (
                        <React.Fragment key={index}>
                            {currentChild === index && (
                                <>
                                    <div className="md:max-w-[400px]">
                                        <ControlledInput
                                            label="Child’s Name"
                                            inputProps={{
                                                id: "childName",
                                                placeholder: "Enter Child Name",
                                            }}
                                            isOptional
                                            isRequired={false}
                                            name={`items.${index}.childName`}
                                            control={control}
                                        />
                                    </div>
                                    <div className="md:max-w-[400px]">
                                        <label
                                            htmlFor={"child-birthday"}
                                            className="font-medium text-[#484554]"
                                        >
                                            Child’s Birthday<span className="text-[#EA5743]">*</span>
                                        </label>
                                        <div className="grid-cols-3 px-1p mt-1 grid gap-3">
                                            <Controller
                                                control={control}
                                                name={`items.${index}.day`}
                                                render={({ field: { ref, onChange, ...rest } }) => (
                                                    <PatternFormat
                                                        format="##"
                                                        {...rest}
                                                        getInputRef={ref}
                                                        isAllowed={(values) => {
                                                            const { floatValue, value } = values;
                                                            return (
                                                                !value || (floatValue <= 31 && floatValue >= 0)
                                                            );
                                                        }}
                                                        onValueChange={({ value }) => {
                                                            onChange(value);
                                                            updateDateString(index, "day", value);
                                                        }}
                                                        onBlur={() => {
                                                            trigger(`items.${index}.day`);
                                                            trigger(`items.${index}.dateString`);
                                                        }}
                                                        error={errors?.items?.[index]?.dateString}
                                                        customInput={EnquiryInput}
                                                        placeholder="DD"
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name={`items.${index}.month`}
                                                render={({ field: { ref, onChange, ...rest } }) => (
                                                    <PatternFormat
                                                        format="##"
                                                        {...rest}
                                                        getInputRef={ref}
                                                        isAllowed={(values) => {
                                                            const { floatValue, value } = values;
                                                            return (
                                                                !value || (floatValue <= 12 && floatValue >= 0)
                                                            );
                                                        }}
                                                        onValueChange={({ value }) => {
                                                            onChange(value);
                                                            updateDateString(index, "month", value);
                                                        }}
                                                        onBlur={() => {
                                                            trigger(`items.${index}.month`);
                                                            trigger(`items.${index}.dateString`);
                                                        }}
                                                        error={errors?.items?.[index]?.dateString}
                                                        customInput={EnquiryInput}
                                                        placeholder="MM"
                                                    />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name={`items.${index}.year`}
                                                render={({ field: { ref, onChange, ...rest } }) => (
                                                    <PatternFormat
                                                        format="####"
                                                        {...rest}
                                                        onValueChange={({ value }) => {
                                                            onChange(value);
                                                            updateDateString(index, "year", value);
                                                        }}
                                                        onBlur={() => {
                                                            trigger(`items.${index}.year`);
                                                            trigger(`items.${index}.dateString`);
                                                        }}
                                                        error={errors?.items?.[index]?.dateString}
                                                        getInputRef={ref}
                                                        customInput={EnquiryInput}
                                                        placeholder="YYYY"
                                                    />
                                                )}
                                            />
                                        </div>
                                        {(errors.items?.[index]?.day?.message ||
                                            errors.items?.[index]?.month?.message ||
                                            errors.items?.[index]?.year?.message) && (
                                                <span className="mt-1 flex-1 flex gap-x-2 items-center text-[#EA5743]">
                                                    <Icon
                                                        className="flex-shrink-0"
                                                        icon="ph:warning-circle"
                                                        width="16"
                                                        height="16"
                                                    />
                                                    {errors.items?.[index]?.day?.message ||
                                                        errors.items?.[index]?.month?.message ||
                                                        errors.items?.[index]?.year?.message}
                                                </span>
                                            )}
                                        {errors.items?.[index]?.dateString?.message && (
                                            <span className="mt-1 flex-1 flex gap-x-2 items-center text-[#EA5743]">
                                                <Icon
                                                    className="flex-shrink-0"
                                                    icon="ph:warning-circle"
                                                    width="16"
                                                    height="16"
                                                />
                                                {errors.items?.[index]?.dateString?.message}
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <label
                                            htmlFor={"child-birthday"}
                                            className="font-medium text-[#3A3A3A]"
                                        >
                                            Care Days<span className="text-[#EA5743]">*</span>
                                        </label>
                                        <ul className="mt-2 flex flex-wrap gap-x-1 gap-y-2">
                                            {careDays.map((item, i) => {
                                                const isActive = childDetailData[index].careDays.find(
                                                    (a) => a.value === item.value
                                                );
                                                return (
                                                    <li
                                                        key={i}
                                                        className={`cursor-pointer py-3 px-4 text-[#3A3A3A] flex justify-center items-center font-medium text-[14px] rounded-full cursor-pointe border ${isActive
                                                                ? "bg-[#EEF2FF] border-[#5A60EC]"
                                                                : "border-white bg-[#F9F7F3]"
                                                            }`}
                                                        onClick={() => {
                                                            onSelectCareDay(item, index);
                                                            trigger(`items.${index}.careDays`);
                                                        }}
                                                    >
                                                        {item.label}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        {errors.items?.[index]?.careDays?.message && (
                                            <span className="mt-1 flex-1 flex gap-x-2 items-center text-[#EA5743]">
                                                <Icon
                                                    className="flex-shrink-0"
                                                    icon="ph:warning-circle"
                                                    width="16"
                                                    height="16"
                                                />
                                                {errors.items?.[index].careDays.message}
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <label
                                            htmlFor={"child-birthday"}
                                            className="font-medium text-[#3A3A3A]"
                                        >
                                            Start Day<span className="text-[#EA5743]">*</span>
                                        </label>

                                        <ul className="mt-2 flex flex-wrap gap-x-1 gap-y-2">
                                            {startDate.map((item, i) => {
                                                const isActive = childDetailData[index].startDate;
                                                return (
                                                    <li
                                                        key={i}
                                                        className={`cursor-pointer py-3 px-4 text-[#3A3A3A] flex justify-center items-center font-medium text-[14px] rounded-full cursor-pointe border ${isActive.value === item.value
                                                                ? "bg-[#EEF2FF] border-[#5A60EC]"
                                                                : "border-white bg-[#F9F7F3]"
                                                            }`}
                                                        onClick={() => {
                                                            handleSelectStartDate(item, index);
                                                            trigger(`items.${index}.startDate`);
                                                        }}
                                                    >
                                                        {item.label}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        {errors.items?.[index]?.startDate?.message && (
                                            <span className="mt-1 flex-1 flex gap-x-2 items-center text-[#EA5743]">
                                                <Icon
                                                    className="flex-shrink-0"
                                                    icon="ph:warning-circle"
                                                    width="16"
                                                    height="16"
                                                />
                                                {errors.items?.[index]?.startDate.message}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}
                        </React.Fragment>
                    );
                })}
                {!props.isPickOptionLiveTour && (
                    <>
                        <div className="bg-[#D9D8DF] w-full !my-[24px] h-[1px]"></div>
                        <ControlledInput
                            label="Additional Information or any questions you may have"
                            inputProps={{
                                id: "comments",
                            }}
                            isOptional={true}
                            isRequired={false}
                            name="comments"
                            control={control}
                            renderInput={({ error, ...props }) => (
                                <textarea
                                    {...props}
                                    rows={4}
                                    placeholder="Add any additional needs your child requires"
                                    className="placeholder:text-[#B1AFAD] w-full border outline-none focus:border-[#5A60EC] hover:border-[#5A60EC] border-[#E3E1DD] bg-white rounded-lg px-2 py-4 resize-none"
                                ></textarea>
                            )}
                        />
                    </>
                )}

                <div className="fixed bottom-0 left-0 right-0 bg-white p-4 !mt-0 md:pt-10 border-t border-gray-200 flex justify-center z-10 lg:static lg:bg-transparent lg:border-0 lg:justify-center lg:ml-[-340px]">
                    <Button
                        onClick={() =>
                            itemsLength > 0 &&
                            document.getElementById("main-content-enquiry").scrollTo(0, 0)
                        }
                        disabled={pickerTimePersonTour?.isDateTimeApiLoading}
                        form="children-detail-form"
                        customClass="w-full lg:w-fit my-0 lg:md:mb-0 child-detail next-button-step-3"
                        type="submit"
                        variant="primary"
                    >
                        {pickerTimePersonTour?.isDateTimeApiLoading ? (
                            <>
                                <span>Next</span>
                                <Icon icon="eos-icons:loading" width="24" height="24" />
                            </>
                        ) : props.isPickOptionLiveTour ? "Next" : (
                            "Finish"
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
}

export default ChildDetailEnquiry;
